import * as React from "react";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  Link,
  Modal as MuiModal,
  TextField,
} from "@mui/material";
import { ClearIcon } from "@mui/x-date-pickers/icons";
import {
  AuthenticationContext,
} from "../../../../components/Context";
import { getHeaders } from "../../../../components/utils/HeaderHelper";
import moment from "moment";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { isSameDay } from "date-fns";
import { useFlash } from "../../../FlashContext";

interface Props {
  showHistoricalImmunization: boolean;
  closeAddHistoricalImmunizationModal: () => void;
  patientId: string;
  refreshImmunizations: () => void;
  selectedImmunization: any;
}

const AddHistoricalImmunization: React.FC<Props> = ({
  showHistoricalImmunization,
  closeAddHistoricalImmunizationModal,
  patientId,
  refreshImmunizations,
  selectedImmunization,
}) => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();
  const [immunizationName, setImmunizationName] = React.useState(
    selectedImmunization?.immunization ?? ""
  );
  const [dateReceived, setDateReceived] = React.useState(
    moment(selectedImmunization?.date_received) || new Date()
  );
  const [loading, setLoading] = React.useState(false);
  const [showDateReceivedPicker, setShowDateReceivedPicker] =
    React.useState<boolean>(false);

  const renderDay = (day, selectedDate, isInCurrentMonth, dayComponent) => {
    const today = new Date();
    const isToday = isSameDay(day, today) && !isSameDay(selectedDate, today);

    return React.cloneElement(dayComponent, {
      style: {
        color: isToday ? "#ff890a" : undefined, // Set color to orange if today
      },
    });
  };

  console.log("dateReceived", dateReceived);
  console.log(
    "selectedImmunization?.date_received",
    selectedImmunization?.date_received
  );

  const handleSubmit = async () => {
    if (!immunizationName) {
      showToast({
        title: "Missing Immunization Name",
        text: "Please enter Immunization Name",
        type: "error",
      });
    } else {
      setLoading(true);
      const historical_immunization = {
        vaccine_name: immunizationName,
        immunization_date: dateReceived,
      };

      const url = selectedImmunization
        ? `/update_historical_immunization/${selectedImmunization.id}`
        : `/create_historical_immunization/${patientId}`;
      const method = selectedImmunization ? "PUT" : "POST";

      try {
        const response = await fetch(url, {
          method,
          headers: getHeaders(authenticationSetting.csrfToken),
          body: JSON.stringify({ historical_immunization }),
        });

        const result = await response.json();

        if (result.success) {
          showToast({
            title: selectedImmunization ? "Historical Immunization Updated" : "Historical Immunization Added",
            text: "Immunization history added successfully",
            type: "success",
          });

          closeAddHistoricalImmunizationModal();
          refreshImmunizations();
        } else {
          showToast({
            title: "Error Saving Immunization History",
            text: "Something went wrong",
            type: "error",
          });
        }
      } catch (error) {
        showToast({
          title: "Error Saving Immunization History",
          text: error.message ?? "Failed to save Immunization history",
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDateChange = (date) => {
    const now = new Date();
    const overLimit =
      date.getDay() == now.getDay() &&
      date.getMonth() == now.getMonth() &&
      date.getFullYear() == now.getFullYear() &&
      date.getTime() > now.getTime();
    if (overLimit) {
      setDateReceived(now);
    } else {
      setDateReceived(date);
    }
  };

  return (
    <MuiModal
      open={showHistoricalImmunization}
      className="modal-primary"
      onClose={closeAddHistoricalImmunizationModal}
    >
      <div className="paper" style={{ width: 430 }}>
        <div className="paperInner">
          <Grid container>
            <Grid item xs={12}>
              <div className="main-header">
                <span>Add Historical Immunization</span>
                <span
                  id="dismiss-button"
                  onClick={closeAddHistoricalImmunizationModal}
                >
                  <ClearIcon sx={{ width: 30, height: 30 }} />
                </span>
              </div>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                paddingLeft: "24px",
                paddingRight: "24px",
                display: "flex",
                flexDirection: "column",
                rowGap: "20px",
              }}
            >
              <Grid container>
                <Grid item xs={12}>
                  <InputLabel
                    htmlFor="immunizationName"
                    className="field-label"
                  >
                    Immunization Name*
                  </InputLabel>
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="immunizationName"
                    size="small"
                    value={immunizationName}
                    className="the-field"
                    required
                    variant="outlined"
                    placeholder="Immunization Name"
                    inputProps={{
                      maxLength: 100
                    }}
                    error={immunizationName.length > 100}
                    helperText={
                      immunizationName.length > 100
                        ? "Maximum length is 100 characters."
                        : ""
                    }
                    onChange={(event) => {
                      const value = event.target.value;
                      if (value.length <= 100) {
                        setImmunizationName(value);
                      }
                    }}
                    sx={{
                      width: "100% !important",
                      backgroundColor: "#efe9e7 !important",
                      borderRadius: "5px",
                      "& fieldset": {
                        border: "none",
                        outline: "none",
                      },
                    }}
                  />
                </Grid>
              </Grid>
              <Box>
                <InputLabel htmlFor="immunizationName" className="field-label">
                  Date Received
                </InputLabel>
                <Box
                  className="field-container"
                  sx={{
                    marginLeft: "0px",
                    backgroundColor: "#efe9e7",
                    display: "flex",
                    borderRadius: "5px",
                  }}
                >
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                      variant="inline"
                      format="MM/dd/yyyy"
                      margin="normal"
                      renderDay={renderDay}
                      id="date-received"
                      value={dateReceived}
                      open={showDateReceivedPicker}
                      onOpen={() => setShowDateReceivedPicker(true)}
                      onClose={() => setShowDateReceivedPicker(false)}
                      onChange={(date) => {
                        handleDateChange(date);
                        setShowDateReceivedPicker(false);
                      }}
                      KeyboardButtonProps={{
                        "aria-label": "change date",
                      }}
                      maxDate={undefined}
                      style={{
                        margin: 0,
                        height: "41.13px",
                        width: "100%",
                      }}
                      InputProps={{
                        disableUnderline: true,
                        inputProps: {
                          placeholder: "mm/dd/yyyy",
                          style: {
                            marginTop: "0",
                            height: "41.13px",
                            padding: "0px 0px 0px 12px",
                          },
                        },
                      }}
                      ToolbarComponent={() => (
                        <Link
                          sx={{
                            fontSize: "14px",
                            order: 1,
                            textAlign: "right",
                            padding: "15px",
                            paddingTop: "0",
                          }}
                          onClick={() => {
                            setDateReceived(new Date());
                            setShowDateReceivedPicker(false);
                          }}
                        >
                          Clear
                        </Link>
                      )}
                    />
                  </MuiPickersUtilsProvider>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            className="modal-footer"
          >
            <Grid item xs={12} className="confirm-btn-container">
              <div
                onClick={closeAddHistoricalImmunizationModal}
                className="cancel-link"
              >
                Cancel
              </div>
              <Button
                onClick={handleSubmit}
                className="confirm-btn"
                style={{ width: 156 }}
              >
                Add Immunization
              </Button>
            </Grid>
          </Grid>
        </div>
        <Backdrop open={loading} style={{ zIndex: 9999, color: "#fff" }}>
          <CircularProgress />
        </Backdrop>
      </div>
    </MuiModal>
  );
};

export default AddHistoricalImmunization;
